import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const SECTION_SLUGS: Record<string, string> = {
  'Лекарства':            'lekarstva',
  'БАДы и витамины':      'bady-i-vitaminy',
  'Дермакосметика':       'dermakosmetika',
  'Гомеопатия':           'gomeopatiya',
  'Контактные линзы':     'kontaktnye-linzy',
  'Медицинские изделия':  'medicinskie-izdeliya',
  'Медицинские приборы':  'medicinskie-pribory',
};

const SECTIONS = [
  {
    id: 1,
    name: 'Лекарства',
    subtitle: 'Рецептурные и безрецептурные препараты',
    icon: 'ri-capsule-line',
    color: '#ff2d78',
    count: '10 000+',
    image: 'https://public.readdy.ai/ai/img_res/35396d7e618313355821f3fd34724a05.jpg',
    tags: ['Сердце и сосуды', 'Антибиотики', 'Обезболивающие'],
    big: true,
  },
  {
    id: 2,
    name: 'БАДы и витамины',
    subtitle: 'Витамины, минералы, омега-3',
    icon: 'ri-sun-line',
    color: '#f0ff00',
    count: '3 500+',
    image: 'https://readdy.ai/api/search-image?query=vitamins%20supplements%20bottles%20capsules%20colorful%20yellow%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography&width=500&height=340&seq=sec-vit&orientation=landscape',
    tags: ['Витамины', 'Минералы', 'Пробиотики'],
    big: false,
  },
  {
    id: 3,
    name: 'Дермакосметика',
    subtitle: 'Уход за кожей, волосами и телом',
    icon: 'ri-sparkling-2-line',
    color: '#ff6b9d',
    count: '2 200+',
    image: 'https://readdy.ai/api/search-image?query=dermocosmetics%20skincare%20cream%20serum%20bottles%20tubes%20arranged%20on%20dark%20surface%20pink%20rose%20neon%20glow%20cyberpunk%20aesthetic%20minimal%20clean%20beauty%20photography&width=500&height=340&seq=sec-derm&orientation=landscape',
    tags: ['Уход за лицом', 'Уход за телом', 'Волосы'],
    big: false,
  },
  {
    id: 4,
    name: 'Гомеопатия',
    subtitle: 'Натуральные гомеопатические средства',
    icon: 'ri-leaf-line',
    color: '#39ff14',
    count: '800+',
    image: 'https://readdy.ai/api/search-image?query=homeopathy%20natural%20herbal%20medicine%20small%20bottles%20granules%20green%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography&width=500&height=340&seq=sec-homeo&orientation=landscape',
    tags: ['Иммунитет', 'Нервная система', 'Суставы'],
    big: false,
  },
  {
    id: 5,
    name: 'Контактные линзы',
    subtitle: 'Линзы, растворы и аксессуары',
    icon: 'ri-eye-line',
    color: '#00f5ff',
    count: '1 200+',
    image: 'https://readdy.ai/api/search-image?query=contact%20lenses%20boxes%20solution%20bottles%20optical%20products%20cyan%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography&width=500&height=340&seq=sec-lens&orientation=landscape',
    tags: ['Однодневные', 'Месячные', 'Растворы'],
    big: false,
  },
  {
    id: 6,
    name: 'Медицинские изделия',
    subtitle: 'Перевязка, тонометры, термометры',
    icon: 'ri-first-aid-kit-line',
    color: '#ff6b00',
    count: '4 000+',
    image: 'https://readdy.ai/api/search-image?query=medical%20devices%20blood%20pressure%20monitor%20thermometer%20bandages%20medical%20supplies%20orange%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography&width=500&height=340&seq=sec-mdev&orientation=landscape',
    tags: ['Тонометры', 'Перевязка', 'Защита'],
    big: false,
  },
  {
    id: 7,
    name: 'Медицинские приборы',
    subtitle: 'Небулайзеры, глюкометры, массажёры',
    icon: 'ri-heart-pulse-line',
    color: '#c77dff',
    count: '900+',
    image: 'https://readdy.ai/api/search-image?query=medical%20equipment%20nebulizer%20glucometer%20pulse%20oximeter%20massager%20devices%20violet%20purple%20neon%20glow%20dark%20background%20cyberpunk%20pharmacy%20product%20minimal%20clean%20photography&width=600&height=700&seq=sec-equip&orientation=portrait',
    tags: ['Ингаляторы', 'Глюкометры', 'Массажёры'],
    big: true,
  },
];

const ProductCategories = () => {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { containerRef: gridRef, visibleItems } = useStaggerReveal(SECTIONS.length, { threshold: 0.05 });

  return (
    <section id="categories" className="py-20 px-6 lg:px-12 relative" style={{ background: '#07070e' }}>
      <div className="grid-bg absolute inset-0 opacity-30" />
      <div className="max-w-7xl mx-auto relative z-10">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4 reveal-hidden ${headerVisible ? 'reveal-up' : ''}`}
        >
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-xs tracking-[0.25em]" style={{ color: 'rgba(255,45,120,0.7)' }}>АССОРТИМЕНТ</span>
              <div className="h-px w-14" style={{ background: 'rgba(255,45,120,0.3)' }} />
            </div>
            <h2 className="font-orbitron font-black leading-tight">
              <span className="block text-white text-3xl md:text-5xl">РАЗДЕЛЫ</span>
              <span
                className="block text-3xl md:text-5xl"
                style={{ color: '#00f5ff', textShadow: '0 0 20px #00f5ff, 0 0 40px #00f5ff' }}
              >
                КАТАЛОГА
              </span>
            </h2>
            <p className="font-mono text-xs text-white/30 mt-3">7 разделов · более 22 000 товаров</p>
          </div>
          <Link
            to="/catalog"
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full font-mono text-xs tracking-widest cursor-pointer transition-all duration-300 self-start md:self-auto"
            style={{ background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.5)', color: '#ff2d78', boxShadow: '0 0 16px rgba(255,45,120,0.2)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 24px rgba(255,45,120,0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 16px rgba(255,45,120,0.2)'; }}
          >
            ВЕСЬ КАТАЛОГ <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Grid layout */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
          style={{ gridTemplateRows: 'auto auto' }}
        >
          {/* Big card LEFT — Лекарства */}
          {(() => {
            const sec = SECTIONS[0];
            const isHovered = hoveredId === sec.id;
            return (
              <Link
                key={sec.id}
                to={`/catalog/section/${SECTION_SLUGS[sec.name]}`}
                onClick={() => {}}
                className={`lg:col-span-2 lg:row-span-2 relative overflow-hidden rounded-2xl cursor-pointer group reveal-hidden ${visibleItems[0] ? 'reveal-scale' : ''}`}
                style={{
                  border: `1px solid ${isHovered ? sec.color : sec.color + '28'}`,
                  minHeight: '420px',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: isHovered ? `0 0 30px ${sec.color}35` : 'none',
                }}
                onMouseEnter={() => setHoveredId(sec.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <img src={sec.image} alt={sec.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" style={{ minHeight: '420px' }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #050508 0%, rgba(5,5,8,0.55) 50%, rgba(5,5,8,0.1) 100%)' }} />

                {/* Count badge */}
                <div className="absolute top-4 left-4 font-mono text-[10px] px-2.5 py-1 rounded-full" style={{ background: `${sec.color}18`, border: `1px solid ${sec.color}50`, color: sec.color }}>
                  {sec.count} товаров
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-3" style={{ background: `${sec.color}15`, border: `1px solid ${sec.color}35` }}>
                    <i className={`${sec.icon} text-xl`} style={{ color: sec.color, filter: `drop-shadow(0 0 6px ${sec.color})` }} />
                  </div>
                  <h3 className="font-orbitron font-black text-2xl mb-1" style={{ color: sec.color, textShadow: `0 0 16px ${sec.color}` }}>
                    {sec.name}
                  </h3>
                  <p className="font-mono text-xs text-white/45 mb-3">{sec.subtitle}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {sec.tags.map(tag => (
                      <span key={tag} className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${sec.color}10`, border: `1px solid ${sec.color}25`, color: `${sec.color}cc` }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 font-mono text-xs transition-colors" style={{ color: isHovered ? sec.color : 'rgba(255,255,255,0.4)' }}>
                    <span>СМОТРЕТЬ ВСЁ</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>

                {/* Top glow line */}
                <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(90deg, transparent, ${sec.color}, transparent)` }} />
              </Link>
            );
          })()}

          {/* Middle 5 small cards */}
          {SECTIONS.slice(1, 6).map((sec, idx) => {
            const isHovered = hoveredId === sec.id;
            return (
              <Link
                key={sec.id}
                to={`/catalog/section/${SECTION_SLUGS[sec.name]}`}
                className={`relative overflow-hidden rounded-2xl cursor-pointer group h-48 lg:h-auto reveal-hidden ${visibleItems[idx + 1] ? 'reveal-up' : ''}`}
                style={{
                  border: `1px solid ${isHovered ? sec.color : sec.color + '20'}`,
                  background: '#0a0a14',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: isHovered ? `0 0 20px ${sec.color}28` : 'none',
                  animationDelay: `${(idx + 1) * 70}ms`,
                  minHeight: '190px',
                }}
                onMouseEnter={() => setHoveredId(sec.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <img src={sec.image} alt={sec.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110 opacity-50 group-hover:opacity-70" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(5,5,8,0.92) 0%, rgba(5,5,8,0.55) 60%, rgba(5,5,8,0.2) 100%)' }} />

                <div className="absolute inset-0 p-4 flex flex-col justify-between">
                  {/* Icon + count */}
                  <div className="flex items-start justify-between">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${sec.color}12`, border: `1px solid ${sec.color}35` }}>
                      <i className={`${sec.icon} text-sm`} style={{ color: sec.color, filter: `drop-shadow(0 0 4px ${sec.color})` }} />
                    </div>
                    <span className="font-mono text-[9px] px-2 py-0.5 rounded-full" style={{ background: `${sec.color}12`, border: `1px solid ${sec.color}30`, color: `${sec.color}bb` }}>
                      {sec.count}
                    </span>
                  </div>

                  {/* Name + tags */}
                  <div>
                    <h3 className="font-orbitron font-bold text-sm mb-1 leading-tight" style={{ color: sec.color, textShadow: `0 0 10px ${sec.color}` }}>
                      {sec.name}
                    </h3>
                    <p className="font-mono text-[10px] text-white/35 mb-2 leading-snug">{sec.subtitle}</p>
                    <div className="flex flex-wrap gap-1">
                      {sec.tags.slice(0, 2).map(tag => (
                        <span key={tag} className="font-mono text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: `${sec.color}08`, border: `1px solid ${sec.color}20`, color: `${sec.color}99` }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Arrow on hover */}
                <div className="absolute bottom-4 right-4 w-7 h-7 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0" style={{ background: sec.color, color: '#050508' }}>
                  <i className="ri-arrow-right-line text-sm" />
                </div>

                {/* Top glow line */}
                <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(90deg, transparent, ${sec.color}, transparent)` }} />
              </Link>
            );
          })}

          {/* Big card RIGHT — Медицинские приборы */}
          {(() => {
            const sec = SECTIONS[6];
            const isHovered = hoveredId === sec.id;
            return (
              <Link
                key={sec.id}
                to={`/catalog/section/${SECTION_SLUGS[sec.name]}`}
                className={`lg:col-span-2 lg:row-span-2 relative overflow-hidden rounded-2xl cursor-pointer group reveal-hidden ${visibleItems[6] ? 'reveal-scale' : ''}`}
                style={{
                  border: `1px solid ${isHovered ? sec.color : sec.color + '28'}`,
                  minHeight: '420px',
                  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: isHovered ? `0 0 30px ${sec.color}35` : 'none',
                  animationDelay: '420ms',
                }}
                onMouseEnter={() => setHoveredId(sec.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <img src={sec.image} alt={sec.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" style={{ minHeight: '420px' }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #050508 0%, rgba(5,5,8,0.55) 50%, rgba(5,5,8,0.1) 100%)' }} />

                {/* Count badge */}
                <div className="absolute top-4 right-4 font-mono text-[10px] px-2.5 py-1 rounded-full" style={{ background: `${sec.color}18`, border: `1px solid ${sec.color}50`, color: sec.color }}>
                  {sec.count} товаров
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-3" style={{ background: `${sec.color}15`, border: `1px solid ${sec.color}35` }}>
                    <i className={`${sec.icon} text-xl`} style={{ color: sec.color, filter: `drop-shadow(0 0 6px ${sec.color})` }} />
                  </div>
                  <h3 className="font-orbitron font-black text-2xl mb-1" style={{ color: sec.color, textShadow: `0 0 16px ${sec.color}` }}>
                    {sec.name}
                  </h3>
                  <p className="font-mono text-xs text-white/45 mb-3">{sec.subtitle}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {sec.tags.map(tag => (
                      <span key={tag} className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${sec.color}10`, border: `1px solid ${sec.color}25`, color: `${sec.color}cc` }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 font-mono text-xs transition-colors" style={{ color: isHovered ? sec.color : 'rgba(255,255,255,0.4)' }}>
                    <span>СМОТРЕТЬ ВСЁ</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>

                {/* Top glow line */}
                <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(90deg, transparent, ${sec.color}, transparent)` }} />
              </Link>
            );
          })()}
        </div>

        {/* Bottom stats row */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: 'ri-store-2-line', value: '22 000+', label: 'товаров в каталоге', color: '#ff2d78' },
            { icon: 'ri-truck-line', value: '2 часа', label: 'доставка по Москве', color: '#00f5ff' },
            { icon: 'ri-shield-check-line', value: '100%', label: 'оригинальные товары', color: '#39ff14' },
            { icon: 'ri-customer-service-2-line', value: '24/7', label: 'поддержка клиентов', color: '#f0ff00' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0" style={{ background: `${stat.color}10`, border: `1px solid ${stat.color}25` }}>
                <i className={`${stat.icon} text-base`} style={{ color: stat.color }} />
              </div>
              <div>
                <div className="font-orbitron font-black text-sm" style={{ color: stat.color }}>{stat.value}</div>
                <div className="font-mono text-[10px] text-white/35 leading-tight">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductCategories;
